import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import { formatearFecha, esFechaDeCalendario } from "../utils/fechas";

const font = "Montserrat, sans-serif";

const SERVICE_LABELS = {
  "belleza-personal":    "Belleza Personal",   "carpinteria":         "Carpintería",
  "cerrajeria":          "Cerrajería",          "decoradores":         "Decoradores",
  "entrenamiento":       "Entrenamiento",       "herrero":             "Herrero",
  "infantiles":          "Infantiles",          "instalador-general":  "Instalador General",
  "jardineria":          "Jardinería",          "mascotas":            "Mascotas",
  "mudanzas":            "Mudanzas",            "pintores":            "Pintores",
  "servicio-domestico":  "Servicio Doméstico",  "tapicero":            "Tapicero",
  "tecnologia":          "Tecnología",          "aire-acondicionado":  "Aire Acondicionado",
  "albanileria":         "Albañilería",         "arquitectura":        "Arquitectura",
  "cuidado-de-mayores":  "Cuidado de Mayores",  "cuidado-de-ninos":    "Cuidado de Niños",
  "electricista":        "Electricista",        "mecanica-automotriz": "Mecánica Automotriz",
  "piletas":             "Piletas",             "plomeros":            "Plomeros",
  "seguridad":           "Seguridad",           "control-de-plagas":   "Control de Plagas",
  "gasista":             "Gasista",
};

const SERVICE_ICONS = {
  "electricista": "⚡", "plomeros": "🔧", "gasista": "🔥", "pintores": "🖌️",
  "albanileria": "🧱", "cerrajeria": "🔑", "servicio-domestico": "🧺",
  "jardineria": "🌿", "aire-acondicionado": "❄️", "arquitectura": "🏛️",
  "belleza-personal": "💇", "carpinteria": "🪵", "control-de-plagas": "🐛",
  "cuidado-de-mayores": "👴", "cuidado-de-ninos": "👶", "decoradores": "🎨",
  "entrenamiento": "💪", "herrero": "⚒️", "infantiles": "🎉",
  "instalador-general": "🔩", "mascotas": "🐾", "mecanica-automotriz": "🚗",
  "mudanzas": "📦", "piletas": "🏊", "seguridad": "🔒",
  "tapicero": "🛋️", "tecnologia": "💻",
};

const STATUS_CONFIG = {
  notified:  { label: "Pendiente",  color: "#b06a00", bg: "rgba(176,106,0,0.1)" },
  accepted:  { label: "Aceptada",   color: "#1b7f4e", bg: "rgba(27,127,78,0.1)" },
  rejected:  { label: "Rechazada",  color: "#c0392b", bg: "rgba(192,57,43,0.1)" },
  expired:   { label: "Expirada",   color: "#7b5ea7", bg: "rgba(123,94,167,0.1)" },
  cancelled: { label: "Cancelada",  color: "#7a93a4", bg: "rgba(0,0,0,0.06)" },
};

// Una notificación en 'notified' puede no estar viva: el plazo ya pasó (el
// backend rechaza aceptarla, matching.js:694) o el cliente canceló la
// solicitud (/payments/cancel no toca los matches). Se muestra como lo que es,
// fuera de "Pendientes" y sin botones, en vez de esperar a que la base cambie.
function estadoVisible(r, ahora) {
  if (r.response_status !== "notified") return r.response_status;
  if (r.request_status === "cancelled") return "cancelled";
  if (r.expires_at && new Date(r.expires_at).getTime() <= ahora) return "expired";
  return "notified";
}

// ─── Timer hook ───────────────────────────────────────────────────────────────
function useCountdown(expiresAt) {
  const calc = useCallback(() => {
    const diff = new Date(expiresAt) - new Date();
    if (diff <= 0) return null;
    const totalSecs = Math.floor(diff / 1000);
    const mins = Math.floor(totalSecs / 60);
    const secs = totalSecs % 60;
    return { mins, secs, totalSecs, urgent: totalSecs < 60 };
  }, [expiresAt]);

  const [time, setTime] = useState(calc);
  useEffect(() => {
    const interval = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(interval);
  }, [calc]);
  return time;
}

// ─── Detail Modal ─────────────────────────────────────────────────────────────
function DetailModal({ req, onClose }) {
  // Dónde es el trabajo: sin esto no se puede presupuestar. La web guarda
  // common_data.ubicacion {direccion, ciudad, provincia}; la app agrega
  // postal_code. ciudad/provincia también van copiadas en la raíz: se muestran
  // acá juntas y se sacan de la lista general para no repetirlas.
  const ub = req.common_data?.ubicacion || {};
  const direccion = [ub.direccion, ub.ciudad || req.common_data?.ciudad, ub.provincia || req.common_data?.provincia, ub.postal_code]
    .filter(Boolean).join(", ");
  const CLAVES_DE_UBICACION = ["ubicacion", "ciudad", "provincia"];
  const commonEntries   = Object.entries(req.common_data || {})
    .filter(([k, v]) => v !== null && v !== "" && v !== undefined && !CLAVES_DE_UBICACION.includes(k));
  const specificEntries = Object.entries(req.specific_data || {}).filter(([, v]) => v !== null && v !== "" && v !== undefined);

  const formatValue = (v) => {
    if (typeof v === "boolean") return v ? "Sí" : "No";
    if (Array.isArray(v)) return v.join(", ");
    if (typeof v === "object") return JSON.stringify(v);
    if (esFechaDeCalendario(v)) return formatearFecha(v);
    return String(v);
  };
  const formatKey = (k) => k.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

  const labelStyle = { fontSize: 10, fontWeight: 700, color: "#7a93a4", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4, display: "block" };
  const valueStyle = { fontSize: 13, color: "#163244", fontWeight: 600, background: "#f5f7fa", borderRadius: 8, padding: "9px 13px", marginBottom: 10, borderLeft: "3px solid rgba(22,50,68,0.12)" };
  const secTitle   = { fontSize: 10, fontWeight: 700, color: "#163244", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 12, paddingBottom: 6, borderBottom: "1.5px solid rgba(0,0,0,0.07)" };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(22,50,68,0.55)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 18, padding: "28px", width: "min(560px, 100%)", maxHeight: "85vh", overflowY: "auto", boxShadow: "0 16px 60px rgba(22,50,68,0.22)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 22 }}>
          <div>
            <h3 style={{ margin: "0 0 4px", color: "#163244", fontSize: 17, fontWeight: 800, fontFamily: font }}>Detalle de la solicitud</h3>
            <div style={{ fontSize: 12, color: "#7a93a4" }}>#{req.request_id} · {SERVICE_LABELS[req.service_key] || req.service_key} · {req.client_name}</div>
          </div>
          <button onClick={onClose} style={{ background: "rgba(0,0,0,0.05)", border: "none", borderRadius: 8, width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 16, color: "#7a93a4" }}>✕</button>
        </div>

        <div style={secTitle}>📍 Ubicación del trabajo</div>
        <div style={valueStyle}>{direccion || "El cliente no cargó la dirección."}</div>

        {commonEntries.length > 0 && (
          <>
            <div style={{ ...secTitle, marginTop: 8 }}>📋 Datos generales</div>
            {commonEntries.map(([k, v]) => (
              <div key={k}><span style={labelStyle}>{formatKey(k)}</span><div style={valueStyle}>{formatValue(v)}</div></div>
            ))}
          </>
        )}
        {specificEntries.length > 0 && (
          <>
            <div style={{ ...secTitle, marginTop: 8 }}>🔧 Detalles del servicio</div>
            {specificEntries.map(([k, v]) => (
              <div key={k}><span style={labelStyle}>{formatKey(k)}</span><div style={valueStyle}>{formatValue(v)}</div></div>
            ))}
          </>
        )}
        {commonEntries.length === 0 && specificEntries.length === 0 && (
          <div style={{ color: "#7a93a4", fontSize: 13, textAlign: "center", padding: "24px 0" }}>No hay datos adicionales.</div>
        )}

        <button onClick={onClose} style={{ marginTop: 8, width: "100%", padding: "11px", borderRadius: 50, border: "1.5px solid rgba(0,0,0,0.12)", background: "transparent", color: "#7a93a4", fontFamily: font, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
          Cerrar
        </button>
      </div>
    </div>
  );
}

// ─── Timers ───────────────────────────────────────────────────────────────────
function UrgentTimer({ expiresAt }) {
  const time = useCountdown(expiresAt);
  if (!time) return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "#c0392b", fontSize: 12, fontWeight: 700, background: "rgba(192,57,43,0.08)", borderRadius: 8, padding: "5px 10px" }}>
      ⏰ Expiró
    </div>
  );
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 7, background: time.urgent ? "rgba(192,57,43,0.08)" : "rgba(176,106,0,0.08)", border: `1.5px solid ${time.urgent ? "rgba(192,57,43,0.3)" : "rgba(176,106,0,0.3)"}`, borderRadius: 9, padding: "6px 12px" }}>
      <span style={{ fontSize: 14 }}>⏱</span>
      <span style={{ fontSize: 16, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: time.urgent ? "#c0392b" : "#b06a00", animation: time.urgent ? "pulse 1s infinite" : "none" }}>
        {String(time.mins).padStart(2, "0")}:{String(time.secs).padStart(2, "0")}
      </span>
      <span style={{ fontSize: 11, color: "#7a93a4" }}>para aceptar</span>
    </div>
  );
}

function QuoteTimer({ expiresAt }) {
  const time = useCountdown(expiresAt);
  if (!time) return <span style={{ color: "#c0392b", fontSize: 11, fontWeight: 600 }}>Expiró</span>;
  const hrs  = Math.floor(time.totalSecs / 3600);
  const mins = Math.floor((time.totalSecs % 3600) / 60);
  return <span style={{ color: "#b06a00", fontSize: 11, fontWeight: 600 }}>⏳ {hrs > 0 ? `${hrs}h ` : ""}{mins}min restantes</span>;
}

// ─── Request Card ─────────────────────────────────────────────────────────────
function RequestCard({ req, onAccept, onReject, loading }) {
  const [detailOpen, setDetailOpen] = useState(false);
  const navigate   = useNavigate();
  const isUrgent   = req.request_type === "urgent";
  const isPending  = req.response_status === "notified";
  const isAccepted = req.response_status === "accepted";
  const isQuote    = req.request_type === "quote";
  const isDim      = ["rejected","expired","cancelled"].includes(req.response_status);
  const icon       = SERVICE_ICONS[req.service_key] || "🛠️";
  const label      = SERVICE_LABELS[req.service_key] || req.service_key;
  const cfg        = STATUS_CONFIG[req.response_status] || STATUS_CONFIG.notified;

  const hasDetails =
    Object.keys(req.common_data   || {}).length > 0 ||
    Object.keys(req.specific_data || {}).length > 0;

  return (
    <>
      <div style={{
        background: "#fff", borderRadius: 16, marginBottom: 12, overflow: "hidden",
        border: `1.5px solid ${isPending && isUrgent ? "#E7A432" : isPending ? "rgba(0,0,0,0.08)" : "rgba(0,0,0,0.07)"}`,
        boxShadow: isPending ? "0 4px 16px rgba(22,50,68,0.08)" : "0 1px 6px rgba(22,50,68,0.04)",
        opacity: isDim ? 0.6 : 1, transition: "all 0.2s",
      }}>
        {/* Strip urgente */}
        {isPending && isUrgent && (
          <div style={{ background: "#E7A432", padding: "5px 18px", fontSize: 10, fontWeight: 800, color: "#163244", letterSpacing: "0.08em", display: "flex", alignItems: "center", gap: 6 }}>
            ⚡ SOLICITUD URGENTE — Respondé rápido
          </div>
        )}

        <div style={{ padding: "16px 18px" }}>
          {/* Top row */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
              <div style={{ width: 42, height: 42, borderRadius: 10, background: isPending ? "#163244" : "#f0f5f8", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>
                {icon}
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", marginBottom: 4 }}>
                  <span style={{ fontSize: 14, fontWeight: 800, color: "#163244" }}>{label}</span>
                  <span style={{
                    background: isUrgent ? "#163244" : "rgba(22,50,68,0.07)",
                    color: isUrgent ? "#E7A432" : "#163244",
                    fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
                  }}>
                    {isUrgent ? "⚡ URGENTE" : "📋 PRESUPUESTO"}
                  </span>
                  <span style={{ padding: "2px 9px", borderRadius: 20, fontSize: 10, fontWeight: 700, color: cfg.color, background: cfg.bg }}>
                    {cfg.label.toUpperCase()}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: "#163244", fontWeight: 600, marginBottom: 2 }}>{req.client_name}</div>
                {req.common_data?.address && (
                  <div style={{ fontSize: 12, color: "#7a93a4", display: "flex", alignItems: "center", gap: 5 }}>
                    📍 {req.common_data.address}
                  </div>
                )}
              </div>
            </div>

            {/* Timer */}
            {isPending && isUrgent  && <UrgentTimer expiresAt={req.expires_at} />}
            {isPending && !isUrgent && <QuoteTimer  expiresAt={req.expires_at} />}
          </div>

          {/* Descripción */}
          {req.common_data?.description && (
            <div style={{ background: "#f8fafb", borderRadius: 9, padding: "10px 13px", fontSize: 13, color: "#163244", marginBottom: 12, borderLeft: "3px solid rgba(22,50,68,0.12)", lineHeight: 1.6, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
              {req.common_data.description}
            </div>
          )}

          {/* Ver detalles */}
          {hasDetails && (
            <button onClick={() => setDetailOpen(true)} style={{ background: "none", border: "1.5px solid rgba(0,0,0,0.1)", borderRadius: 8, cursor: "pointer", fontSize: 12, color: "#163244", fontWeight: 600, padding: "6px 13px", marginBottom: 12, fontFamily: font, display: "inline-flex", alignItems: "center", gap: 5 }}>
              🔍 Ver todos los detalles
            </button>
          )}

          {/* Acciones pendiente */}
          {isPending && (
            <div style={{ display: "flex", gap: 9 }}>
              <button onClick={() => onAccept(req.match_id, req)} disabled={!!loading} style={{
                flex: 1, padding: "11px", borderRadius: 50, border: "none",
                background: "#163244", color: "#fff", fontFamily: font, fontWeight: 700,
                fontSize: 13, cursor: loading ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}>
                {loading === `accept-${req.match_id}`
                  ? <span style={{ width: 14, height: 14, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} />
                  : isUrgent ? "⚡ Aceptar trabajo" : "📋 Enviar presupuesto"}
              </button>
              <button onClick={() => onReject(req.match_id)} disabled={!!loading} style={{
                padding: "11px 20px", borderRadius: 50, border: "1.5px solid rgba(0,0,0,0.12)",
                background: "transparent", color: "#7a93a4", fontFamily: font, fontWeight: 600,
                fontSize: 13, cursor: loading ? "not-allowed" : "pointer",
              }}>
                Rechazar
              </button>
            </div>
          )}

          {/* Acciones aceptada */}
          {isAccepted && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid rgba(0,0,0,0.06)", display: "flex", gap: 9 }}>
              {isQuote && (
                <button onClick={() => navigate("/prestador/presupuestos", { state: { openQuoteModal: true, requestId: req.request_id, label: `#${req.request_id} — ${label} — ${req.client_name}` } })} style={{
                  flex: 1, padding: "10px", borderRadius: 50, background: "#163244", border: "none",
                  color: "#fff", fontFamily: font, fontWeight: 700, fontSize: 13, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                }}>
                  📋 Enviar presupuesto
                </button>
              )}
              <button onClick={() => navigate(`/chat/${req.request_id}`)} style={{
                flex: 1, padding: "10px", borderRadius: 50, border: "1.5px solid #163244",
                background: "#fff", color: "#163244", fontFamily: font, fontWeight: 700,
                fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}>
                💬 Chat con cliente
              </button>
            </div>
          )}
        </div>
      </div>

      {detailOpen && <DetailModal req={req} onClose={() => setDetailOpen(false)} />}
    </>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────
function Sidebar({ requests }) {
  const pending   = requests.filter(r => r.response_status === "notified").length;
  const accepted  = requests.filter(r => r.response_status === "accepted").length;
  const rejected  = requests.filter(r => r.response_status === "rejected").length;
  const expired   = requests.filter(r => r.response_status === "expired").length;
  const total     = requests.length;
  const urgent    = requests.filter(r => r.response_status === "notified" && r.request_type === "urgent").length;
  const responded = accepted + rejected + expired;
  const responseRate = total > 0 ? Math.round((responded / total) * 100) : 0;

  // Top servicios
  const svcCount = {};
  requests.forEach(r => { const l = SERVICE_LABELS[r.service_key] || r.service_key; svcCount[l] = (svcCount[l] || 0) + 1; });
  const topSvcs = Object.entries(svcCount).sort((a, b) => b[1] - a[1]).slice(0, 4);

  const sCard = { background: "#fff", borderRadius: 14, border: "1px solid rgba(0,0,0,0.07)", overflow: "hidden", marginBottom: 14, fontFamily: font };
  const sHdr  = { padding: "14px 16px 0", display: "flex", alignItems: "center", gap: 8, marginBottom: 12 };
  const sAcc  = { width: 3, height: 13, background: "#E7A432", borderRadius: 2, flexShrink: 0 };
  const sTit  = { fontSize: 11, fontWeight: 700, color: "#163244", textTransform: "uppercase", letterSpacing: 1 };

  return (
    <div style={{ fontFamily: font }}>

      {/* Alert urgentes */}
      {urgent > 0 && (
        <div style={{ background: "#163244", borderRadius: 14, padding: "16px 18px", marginBottom: 14, position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle at 85% 15%, rgba(231,164,50,0.18) 0%, transparent 55%)", pointerEvents: "none" }} />
          <div style={{ position: "relative", zIndex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 20 }}>⚡</span>
              <span style={{ color: "#E7A432", fontWeight: 800, fontSize: 14 }}>{urgent} urgente{urgent > 1 ? "s" : ""} pendiente{urgent > 1 ? "s" : ""}</span>
            </div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", lineHeight: 1.55 }}>
              Las solicitudes urgentes expiran en minutos. Respondé cuanto antes para no perderlas.
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div style={sCard}>
        <div style={sHdr}><div style={sAcc}/><span style={sTit}>Resumen</span></div>
        <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <div style={{ background: "#f5f7fa", borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#163244", lineHeight: 1 }}>{pending}</div>
              <div style={{ fontSize: 10, color: "#7a93a4", marginTop: 3, fontWeight: 500 }}>Pendientes</div>
            </div>
            <div style={{ background: "#f5f7fa", borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#1b7f4e", lineHeight: 1 }}>{accepted}</div>
              <div style={{ fontSize: 10, color: "#7a93a4", marginTop: 3, fontWeight: 500 }}>Aceptadas</div>
            </div>
          </div>

          {/* Tasa de respuesta */}
          {total > 0 && (
            <div style={{ background: "#f5f7fa", borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontSize: 10, color: "#7a93a4", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Tasa de respuesta</span>
                <span style={{ fontSize: 14, fontWeight: 900, color: responseRate >= 70 ? "#1b7f4e" : "#b06a00" }}>{responseRate}%</span>
              </div>
              <div style={{ height: 4, borderRadius: 2, background: "#e0e9ef", overflow: "hidden" }}>
                <div style={{ height: "100%", borderRadius: 2, background: responseRate >= 70 ? "#1b7f4e" : "#E7A432", width: `${responseRate}%`, transition: "width 0.5s" }} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Top servicios */}
      {topSvcs.length > 0 && (
        <div style={sCard}>
          <div style={sHdr}><div style={sAcc}/><span style={sTit}>Por servicio</span></div>
          <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 6 }}>
            {topSvcs.map(([lbl, cnt], i) => (
              <div key={lbl} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: i < topSvcs.length - 1 ? "1px solid rgba(0,0,0,0.05)" : "none" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, background: ["#163244","#E7A432","rgba(22,50,68,0.35)","rgba(231,164,50,0.4)"][i] }} />
                <span style={{ fontSize: 12, color: "#5a6e7f", flex: 1 }}>{lbl}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#163244" }}>{cnt}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tip */}
      <div style={sCard}>
        <div style={{ padding: "14px 16px" }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, background: "rgba(231,164,50,0.1)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 10 }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#E7A432" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#163244", marginBottom: 4 }}>Respondé rápido</div>
          <div style={{ fontSize: 12, color: "#7a93a4", lineHeight: 1.55 }}>
            Los prestadores que responden en menos de 30 min tienen un 60% más de chances de ser contratados.
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ProviderRequestsPage() {
  const navigate = useNavigate();
  const [crudas,      setRequests]    = useState([]);
  const [ahora,       setAhora]       = useState(Date.now());
  const [loadingData, setLoadingData] = useState(true);
  const [loadError,   setLoadError]   = useState("");
  const [filter,      setFilter]      = useState("pending");
  const [page,        setPage]        = useState(1);
  const PAGE_SIZE = 5;
  const [loading,     setLoading]     = useState(null);
  const [toast,       setToast]       = useState(null);

  // Reloj de la lista: una tarjeta que vence mientras está abierta pasa sola a
  // Historial (como mucho 15 s tarde; en esa ventana el backend igual rechaza).
  useEffect(() => {
    const t = setInterval(() => setAhora(Date.now()), 15000);
    return () => clearInterval(t);
  }, []);
  const requests = crudas.map(r => ({ ...r, response_status: estadoVisible(r, ahora) }));

  useEffect(() => {
    setLoadingData(true); setLoadError("");
    api.get("/matching/provider/requests")
      .then(res => setRequests(res.data.requests || []))
      .catch(() => setLoadError("No se pudieron cargar las solicitudes."))
      .finally(() => setLoadingData(false));
  }, []);

  // Rubros vencidos y perfil suspendido: el emparejamiento los excluye en
  // silencio, y sin este aviso una lista vacía se lee como "la app no me manda
  // nada". El único aviso que había estaba en Mi perfil → Servicios.
  const [bloqueos, setBloqueos] = useState({ vencidos: [], suspendido: false });
  useEffect(() => {
    api.get("/providers/me")
      .then(res => {
        const p = res.data.provider || res.data;
        setBloqueos({
          vencidos:   (p.services || []).filter(s => s.status === "expired").map(s => s.service_name || s.slug),
          suspendido: p.status === "suspended",
        });
      })
      .catch(() => {}); // el aviso es accesorio: si falla, la pantalla sigue igual
  }, []);

  const showToast = (msg, type = "success") => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const handleAccept = async (matchId, req) => {
    setLoading(`accept-${matchId}`);
    let shouldNavigate = false;
    try {
      await api.post(`/matching/provider/requests/${matchId}/accept`);
      setRequests(rs => rs.map(r => r.match_id === matchId ? { ...r, response_status: "accepted" } : r));
      if (req?.request_type === "quote") shouldNavigate = true;
      else showToast("¡Solicitud aceptada! El cliente fue notificado.");
    } catch (err) {
      showToast(err.response?.data?.error || "Error al aceptar la solicitud.", "error");
    } finally { setLoading(null); }
    if (shouldNavigate) navigate("/prestador/presupuestos", { state: { openQuoteModal: true, requestId: req.request_id, label: `#${req.request_id} — ${SERVICE_LABELS[req.service_key] || req.service_key} — ${req.client_name}` } });
  };

  const handleReject = async (matchId) => {
    setLoading(`reject-${matchId}`);
    try {
      await api.post(`/matching/provider/requests/${matchId}/reject`);
      setRequests(rs => rs.map(r => r.match_id === matchId ? { ...r, response_status: "rejected" } : r));
      showToast("Solicitud rechazada.", "info");
    } catch (err) {
      showToast(err.response?.data?.error || "Error al rechazar.", "error");
    } finally { setLoading(null); }
  };

  const FILTERS = [
    { id: "pending",  label: "Pendientes", count: requests.filter(r => r.response_status === "notified").length },
    { id: "accepted", label: "Aceptadas",  count: requests.filter(r => r.response_status === "accepted").length },
    { id: "history",  label: "Historial",  count: requests.filter(r => ["rejected","expired","cancelled"].includes(r.response_status)).length },
  ];

  const filtered = requests.filter(r => {
    if (filter === "pending")  return r.response_status === "notified";
    if (filter === "accepted") return r.response_status === "accepted";
    if (filter === "history")  return ["rejected","expired","cancelled"].includes(r.response_status);
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (a.request_type === "urgent" && b.request_type !== "urgent") return -1;
    if (b.request_type === "urgent" && a.request_type !== "urgent") return  1;
    return new Date(b.notified_at) - new Date(a.notified_at);
  });

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const paginated  = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const pendingUrgent = requests.filter(r => r.response_status === "notified" && r.request_type === "urgent").length;

  return (
    <>
      <div style={{ fontFamily: font, background: "#f5f7fa", minHeight: "100vh", padding: "28px 24px 60px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 290px", gap: 20, alignItems: "start" }}>

            {/* Columna izquierda */}
            <div>
              {(bloqueos.suspendido || bloqueos.vencidos.length > 0) && (
                <div style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 12, padding: "14px 16px", marginBottom: 18, fontSize: 13, color: "#163244", lineHeight: 1.55 }}>
                  <div style={{ fontWeight: 800, color: "#EF4444", marginBottom: 4 }}>
                    ⚠ {bloqueos.suspendido ? "Tu perfil está suspendido" : `Rubro vencido: ${bloqueos.vencidos.join(", ")}`}
                  </div>
                  {bloqueos.suspendido
                    ? "No vas a recibir solicitudes nuevas. Escribinos para reactivarlo. "
                    : `No vas a recibir solicitudes de ${bloqueos.vencidos.length > 1 ? "esos rubros" : "ese rubro"}. Subí la documentación nueva desde tu perfil: cuando la aprobemos, se reactiva. `}
                  <span onClick={() => navigate("/prestador/perfil")} style={{ color: "#163244", fontWeight: 700, textDecoration: "underline", cursor: "pointer" }}>
                    Ir a mi perfil
                  </span>
                </div>
              )}
              {/* Header */}
              <div style={{ marginBottom: 18 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                  <h1 style={{ margin: 0, fontSize: 21, fontWeight: 800, color: "#163244" }}>Mis solicitudes</h1>
                  {pendingUrgent > 0 && (
                    <span style={{ background: "#c0392b", color: "#fff", borderRadius: 20, padding: "3px 11px", fontSize: 12, fontWeight: 800, animation: "pulse 1.5s infinite" }}>
                      {pendingUrgent} urgente{pendingUrgent > 1 ? "s" : ""}
                    </span>
                  )}
                </div>
                <p style={{ margin: 0, color: "#7a93a4", fontSize: 13 }}>
                  Respondé las solicitudes urgentes antes de que expiren.
                </p>
              </div>

              {/* Filtros */}
              {!loadingData && !loadError && (
                <div style={{ display: "flex", gap: 5, marginBottom: 16, background: "#fff", borderRadius: 12, padding: 5, border: "1px solid rgba(0,0,0,0.07)" }}>
                  {FILTERS.map(f => (
                    <button key={f.id} onClick={() => { setFilter(f.id); setPage(1); }} style={{
                      flex: 1, padding: "8px 6px", borderRadius: 9, border: "none",
                      background: filter === f.id ? "#163244" : "transparent",
                      color: filter === f.id ? "#fff" : "#5a6e7f",
                      fontFamily: font, fontWeight: filter === f.id ? 700 : 500,
                      fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center",
                      justifyContent: "center", gap: 5, transition: "all 0.15s",
                    }}>
                      {f.label}
                      <span style={{ borderRadius: 10, padding: "1px 6px", fontSize: 11, fontWeight: 800, background: filter === f.id ? "#E7A432" : "#e8eef2", color: filter === f.id ? "#163244" : "#5a6e7f" }}>
                        {f.count}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* Loading */}
              {loadingData && (
                <div style={{ textAlign: "center", padding: "60px", color: "#7a93a4" }}>
                  <span style={{ display: "inline-block", width: 26, height: 26, border: "3px solid #dce5ec", borderTopColor: "#163244", borderRadius: "50%", animation: "spin 0.7s linear infinite", marginBottom: 12 }} /><br/>
                  Cargando solicitudes...
                </div>
              )}

              {/* Error */}
              {loadError && !loadingData && (
                <div style={{ textAlign: "center", padding: "40px", background: "#fff", borderRadius: 14, color: "#c0392b", fontSize: 14 }}>
                  ⚠ {loadError}
                </div>
              )}

              {/* Empty */}
              {!loadingData && !loadError && sorted.length === 0 && (
                <div style={{ textAlign: "center", padding: "56px 20px", background: "#fff", borderRadius: 16, border: "2px dashed rgba(0,0,0,0.08)", color: "#aab8c2" }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
                  <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6, color: "#163244" }}>
                    {filter === "pending" ? "No tenés solicitudes pendientes" : "No hay solicitudes en esta categoría"}
                  </div>
                  <div style={{ fontSize: 13 }}>
                    {filter === "pending" ? "Cuando recibas nuevas solicitudes aparecerán acá." : "Aquí verás el historial de tus solicitudes."}
                  </div>
                </div>
              )}

              {/* Cards paginadas */}
              {!loadingData && !loadError && paginated.map(req => (
                <RequestCard key={req.match_id} req={req} onAccept={handleAccept} onReject={handleReject} loading={loading} />
              ))}

              {/* Paginación */}
              {!loadingData && !loadError && totalPages > 1 && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 8 }}>
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                    style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid rgba(0,0,0,0.1)", background: "#fff", cursor: page === 1 ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#163244", opacity: page === 1 ? 0.35 : 1 }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <button key={p} onClick={() => setPage(p)}
                      style={{ width: 32, height: 32, borderRadius: 8, border: page === p ? "none" : "1px solid rgba(0,0,0,0.1)", background: page === p ? "#163244" : "#fff", color: page === p ? "#fff" : "#5a6e7f", fontFamily: font, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                      {p}
                    </button>
                  ))}
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                    style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid rgba(0,0,0,0.1)", background: "#fff", cursor: page === totalPages ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#163244", opacity: page === totalPages ? 0.35 : 1 }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
                  </button>
                  <span style={{ fontSize: 12, color: "#7a93a4", marginLeft: 4 }}>
                    {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, sorted.length)} de {sorted.length}
                  </span>
                </div>
              )}
            </div>

            {/* Sidebar */}
            {!loadingData && !loadError && <Sidebar requests={requests} />}
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: "#163244", color: "#fff", padding: "13px 26px", borderRadius: 10,
          fontWeight: 600, fontSize: 14, zIndex: 999, fontFamily: font,
          boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
          borderLeft: `4px solid ${toast.type === "success" ? "#E7A432" : "#cddae4"}`,
          whiteSpace: "nowrap",
        }}>
          {toast.msg}
        </div>
      )}

      <style>{`
        * { box-sizing: border-box; }
        @keyframes spin  { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.6; } }
      `}</style>
    </>
  );
}
